import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, ArrowRight, BookOpen, LifeBuoy, Loader2, ShieldAlert } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  AUTHORITY_NOTE,
  CONTESTED_OPTIONS,
  CONTESTED_QUESTION,
  CONTESTED_STOP,
  ENDING_OPTIONS,
  ORIENTATION_DO,
  ORIENTATION_KNOW,
  ORIENTATION_SCHEDULE,
  PASSING_FAMILY_NOTE,
  SEQUENCING_TEACHING,
  routeExit,
  todayIso,
  type ContestedAnswer,
  type EndingKind,
  type ExitWorkflowData,
  type YesNo,
} from './exitContent';

interface ExitPhase1Props {
  task: any;
  phaseData?: any;
  onPhaseComplete: () => void;
}

const STEP_TITLES = ['Start here', 'One question first', 'Which ending is this?', 'About the ending', 'Review'];

function AskAPerson() {
  return (
    <Button asChild variant="outline" size="sm" className="min-h-11">
      <a href="/support/ask">
        <LifeBuoy className="h-4 w-4 mr-2" />
        Ask a person
      </a>
    </Button>
  );
}

function YesNoField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value?: YesNo;
  onChange: (v: YesNo) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <RadioGroup value={value} onValueChange={(v) => onChange(v as YesNo)} className="flex gap-6">
        <div className="flex items-center gap-2">
          <RadioGroupItem value="yes" id={`${id}-yes`} />
          <Label htmlFor={`${id}-yes`} className="font-normal cursor-pointer">
            Yes
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="no" id={`${id}-no`} />
          <Label htmlFor={`${id}-no`} className="font-normal cursor-pointer">
            No
          </Label>
        </div>
      </RadioGroup>
    </div>
  );
}

export function ExitPhase1({ task, onPhaseComplete }: ExitPhase1Props) {
  const { toast } = useToast();
  const existing: ExitWorkflowData = task?.workflow_data?.exit || {};

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [unaName, setUnaName] = useState('');

  const [contested, setContested] = useState<ContestedAnswer | undefined>(existing.contested);
  const [ending, setEnding] = useState<EndingKind | undefined>(existing.ending);

  const [memberName, setMemberName] = useState(existing.member_name || '');
  const [effectiveDate, setEffectiveDate] = useState(existing.effective_date || todayIso());
  const [heldOffice, setHeldOffice] = useState<YesNo | undefined>(existing.held_office);
  const [heldBanking, setHeldBanking] = useState<YesNo | undefined>(existing.held_banking);
  const [bankName, setBankName] = useState(existing.bank_name || '');
  const [gratitudeLine, setGratitudeLine] = useState(existing.gratitude_line || '');

  const [passingDate, setPassingDate] = useState(existing.passing_date || todayIso());
  const [passingAuthorities, setPassingAuthorities] = useState<YesNo | undefined>(existing.passing_authorities);
  const [remembranceLine, setRemembranceLine] = useState(existing.remembrance_line || '');

  const [notes, setNotes] = useState(existing.notes || '');

  useEffect(() => {
    async function loadUna() {
      if (!task?.una_id) return;
      const { data } = await supabase.from('unas').select('name').eq('id', task.una_id).maybeSingle();
      if (data?.name) setUnaName(data.name);
    }
    loadUna();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.una_id]);

  const data: ExitWorkflowData = useMemo(
    () => ({
      contested,
      ending,
      member_name: memberName,
      effective_date: effectiveDate,
      held_office: heldOffice,
      held_banking: heldBanking,
      bank_name: bankName,
      gratitude_line: gratitudeLine,
      passing_date: passingDate,
      passing_authorities: passingAuthorities,
      remembrance_line: remembranceLine,
      notes,
    }),
    [
      contested,
      ending,
      memberName,
      effectiveDate,
      heldOffice,
      heldBanking,
      bankName,
      gratitudeLine,
      passingDate,
      passingAuthorities,
      remembranceLine,
      notes,
    ],
  );

  const route = routeExit(data);
  const contestedBlocked = contested === 'yes' || contested === 'not_sure';

  async function persist() {
    setSaving(true);
    try {
      const nextData = { ...(task.workflow_data || {}), exit: data };
      await supabase.from('task_workflows').update({ workflow_data: nextData }).eq('task_id', task.id);
      if (task.workflow_data) task.workflow_data.exit = data;
    } catch (e: any) {
      console.error('Could not save the ending answers', e);
      toast({
        title: 'We could not save that',
        description: e.message || 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  function canContinue(): boolean {
    if (step === 2) return contested === 'no';
    if (step === 3) return !!ending;
    if (step === 4) {
      if (contestedBlocked) return false;
      if (!memberName.trim()) return false;
      if (ending === 'withdrawal') return !!heldOffice && !!heldBanking && !!effectiveDate;
      return !!passingAuthorities && !!passingDate;
    }
    return true;
  }

  async function next() {
    await persist();
    setStep((s) => Math.min(5, s + 1));
  }

  async function finish() {
    await persist();
    toast({ title: 'Saved', description: 'Your answers are saved. Next you prepare the papers.' });
    onPhaseComplete();
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-base">
              Step {step} of 5: {STEP_TITLES[step - 1]}
            </CardTitle>
            {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <div className="space-y-4 text-sm leading-relaxed">
              <div>
                <h4 className="font-medium mb-1">What you need to know</h4>
                <p>{ORIENTATION_KNOW}</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">What you need to do</h4>
                <p>{ORIENTATION_DO}</p>
              </div>
              <div>
                <h4 className="font-medium mb-1">Your schedule</h4>
                <p>{ORIENTATION_SCHEDULE}</p>
              </div>
              <Button asChild variant="outline" size="sm" className="min-h-11">
                <a href="/support/library">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Read the membership guides in the library
                </a>
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm">{CONTESTED_QUESTION}</p>
              <RadioGroup
                value={contested}
                onValueChange={(v) => setContested(v as ContestedAnswer)}
                className="space-y-3"
              >
                {CONTESTED_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value={opt.value} id={`contested-${opt.value}`} className="mt-1" />
                    <Label htmlFor={`contested-${opt.value}`} className="font-normal cursor-pointer">
                      <span className="font-medium block">{opt.label}</span>
                      <span className="text-sm text-muted-foreground">{opt.helper}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {contestedBlocked && (
                <Alert className="border-destructive/50">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertDescription className="space-y-3 text-sm">
                    <p>{CONTESTED_STOP}</p>
                    <p>No papers prepare while this is the answer.</p>
                    <AskAPerson />
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Which ending is this?</p>
              <RadioGroup value={ending} onValueChange={(v) => setEnding(v as EndingKind)} className="space-y-3">
                {ENDING_OPTIONS.map((opt) => (
                  <div key={opt.value} className="flex items-start gap-3 rounded-lg border p-3">
                    <RadioGroupItem value={opt.value} id={`ending-${opt.value}`} className="mt-1" />
                    <Label htmlFor={`ending-${opt.value}`} className="font-normal cursor-pointer">
                      <span className="font-medium block">{opt.label}</span>
                      <span className="text-sm text-muted-foreground">{opt.helper}</span>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {step === 4 && ending === 'withdrawal' && (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="exit-member">Member's name</Label>
                  <Input id="exit-member" value={memberName} onChange={(e) => setMemberName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="exit-effective">Effective date</Label>
                  <Input
                    id="exit-effective"
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                  />
                </div>
              </div>
              <YesNoField id="held-office" label="Did they hold any office?" value={heldOffice} onChange={setHeldOffice} />
              <YesNoField
                id="held-banking"
                label="Did they hold banking authority?"
                value={heldBanking}
                onChange={setHeldBanking}
              />
              {heldBanking === 'yes' && (
                <div className="space-y-2">
                  <Label htmlFor="exit-bank">Which bank</Label>
                  <Input id="exit-bank" value={bankName} onChange={(e) => setBankName(e.target.value)} />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="exit-gratitude">A line of thanks for the record, if you want one</Label>
                <Textarea
                  id="exit-gratitude"
                  value={gratitudeLine}
                  onChange={(e) => setGratitudeLine(e.target.value)}
                  rows={3}
                />
              </div>
              {(heldOffice === 'yes' || heldBanking === 'yes') && (
                <Alert>
                  <AlertDescription className="text-sm">{AUTHORITY_NOTE}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {step === 4 && ending === 'passing' && (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="pass-member">Member's name</Label>
                  <Input id="pass-member" value={memberName} onChange={(e) => setMemberName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pass-date">Date of passing</Label>
                  <Input
                    id="pass-date"
                    type="date"
                    value={passingDate}
                    onChange={(e) => setPassingDate(e.target.value)}
                  />
                </div>
              </div>
              <YesNoField
                id="pass-authorities"
                label="Did they hold an office or banking authority?"
                value={passingAuthorities}
                onChange={setPassingAuthorities}
              />
              {passingAuthorities === 'yes' && (
                <div className="space-y-2">
                  <Label htmlFor="pass-bank">Which bank, if there was banking authority</Label>
                  <Input id="pass-bank" value={bankName} onChange={(e) => setBankName(e.target.value)} />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="pass-remembrance">A line of remembrance for the minutes</Label>
                <Textarea
                  id="pass-remembrance"
                  value={remembranceLine}
                  onChange={(e) => setRemembranceLine(e.target.value)}
                  rows={3}
                />
              </div>
              <Alert>
                <AlertDescription className="text-sm">{PASSING_FAMILY_NOTE}</AlertDescription>
              </Alert>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5 text-sm">
              {unaName && <p className="text-muted-foreground">These papers prepare for {unaName}.</p>}
              <div className="space-y-2">
                <Label htmlFor="exit-notes">Anything the record should say</Label>
                <Textarea id="exit-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
              </div>
              <Alert>
                <AlertDescription className="space-y-2 text-sm">
                  <p>{route.summary}</p>
                  <p>{SEQUENCING_TEACHING}</p>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <Button
              variant="outline"
              className="min-h-11"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {step < 5 ? (
              <Button onClick={next} disabled={!canContinue() || saving} className="min-h-11">
                Continue
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={finish} disabled={saving || route.hardStop} className="min-h-11">
                Save and go to the papers
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ExitPhase1;
